import {
  Injectable, Output, EventEmitter, Directive, Renderer2,
} from '@angular/core';
import { Subscription, asapScheduler, interval } from 'rxjs';
import { RestApiService, ModalService, AnalyticsService } from '@ui-kit';
import { merge, get } from 'lodash-es';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { clearSubscription } from './app.helpers';
import { UserService } from './shared/service';
import { environment } from '../environments/environment';

export type appEventType = 'homes-list-scroll-top'
  | 'trigger-home-budget-update'
  | 'trigger-resize'
  | 'trigger-update'
  | 'trigger-web-view'
  | 'add-a-home'
  | 'added-a-home'
  | 'favourite-focus'
  | 'favourite-blur'
  | 'dismiss-add-a-home'
  | 'offer-accepted'
  | 'offer-accepted-confirm'
  | 'refer-to-broker'
  | 'refer-to-broker-confirm'
  | 'book-a-call'
  | 'booked-a-call'
  | 'book-a-call-update'
  | 'close-book-a-call'
  | 'scroll-to'
  | 'scroll-top'
  | 'trigger-pinterest'
  | 'trigger-rightmove'
  | 'working';

export interface AppEvent {
  type: appEventType;
  data?: any;
}

export interface AppWebView {
  url: string;
  label: string;
  action: string;
  actionUrl: string;
}

@Directive()
@Injectable({
  providedIn: 'root',
})
// tslint:disable-next-line: directive-class-suffix
export class AppService {
  @Output() event: EventEmitter<AppEvent> = new EventEmitter();

  get window(): {
    zESettings?: any
  } & Window {
    return window;
  }

  get standalone(): boolean {
    return (navigator as any).standalone
      || matchMedia('(display-mode: standalone)').matches;
  }

  private subscription: Dictionary<Subscription> = {};

  constructor(
    private user: UserService,
    private restApi: RestApiService,
    private renderer: Renderer2,
    private router: Router,
    private modal: ModalService,
    private analytics: AnalyticsService,
  ) {
    this.user.event
      .subscribe((event) => {
        this.analytics.eventTrack({
          data: {
            event: 'http',
            action: 'user-event',
            label: event.type,
            properties: {
              value: '1',
              category: 'app_auth',
              route: this.router.url,
            },
          },
        });
      });
    // TODO: env staging console.log, env prod sentry
    this.restApi.error
      .subscribe(console.log);
  }

  /**
   * update window object
   */
  public updateWindowObject(key: string, update: any): void {
    const obj = window[key] || {};
    window[obj] = merge(obj, update);
  }

  /**
   * target 3rd party window objects
   */
  public windowObject(
    key: 'zE' | 'Calendly' | 'Trustpilot' | 'appzi',
    prop: string,
    callback: (obj: any) => void,
    delay: number = 300,
  ): void {
    let isLoaded: boolean;
    switch (key) {
      case 'zE':
        isLoaded = !!document?.querySelector('#ze-snippet');
        if (!isLoaded) {
          // zendesk settings
          this.window.zESettings = environment.zendeskSettings;
          // load zendesk script
          const zendeskScript = document?.createElement('script');
          zendeskScript.id = 'ze-snippet';
          zendeskScript.src = '//static.zdassets.com/ekr/snippet.js?key=ab9240c5-2a2d-4536-93a3-5a1beceeff7b';
          zendeskScript.type = 'text/javascript';
          this.renderer.appendChild(document?.body, zendeskScript);
          // hide zendesk by default
          this.windowObject('zE', 'hide', (zendesk) => {
            zendesk('webWidget', 'hide');
            zendesk('webWidget:on', 'chat:unreadMessages', (n: number) => {
              if (zendesk?.activate) {
                zendesk.activate({ hideOnClose: true });
              }
            });
          }, 0);
        }
        break;
      case 'Calendly':
        isLoaded = !!document?.querySelector('#calendly-snippet');
        if (!isLoaded) {
          // load calendly style
          const calendlyLink = document?.createElement('link');
          calendlyLink.id = 'calendly-snippet';
          calendlyLink.href = '//assets.calendly.com/assets/external/widget.css';
          calendlyLink.rel = 'stylesheet';
          this.renderer.appendChild(document?.body, calendlyLink);
          // load calendly script
          const calendlyScript = document?.createElement('script');
          calendlyScript.src = '//assets.calendly.com/assets/external/widget.js';
          calendlyScript.type = 'text/javascript';
          this.renderer.appendChild(document?.body, calendlyScript);
        }
        break;
      case 'Trustpilot':
        isLoaded = !!document?.querySelector('#trustpilot-snippet');
        if (!isLoaded) {
          // load trustpilot script
          const trustpilotScript = document?.createElement('script');
          trustpilotScript.id = 'trustpilot-snippet';
          trustpilotScript.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
          trustpilotScript.type = 'text/javascript';
          trustpilotScript.async = true;
          this.renderer.appendChild(document?.body, trustpilotScript);
        }
        break;
      case 'appzi':
        isLoaded = !!document?.querySelector('#appzi-snippet');
        if (!isLoaded) {
          // load appzi script
          const appziScript = document?.createElement('script');
          appziScript.id = 'appzi-snippet';
          appziScript.src = 'https://w.appzi.io/bootstrap/bundle.js?token=KqldC';
          appziScript.type = 'text/javascript';
          appziScript.async = true;
          this.renderer.appendChild(document?.body, appziScript);
        }
        break;
      default:
        isLoaded = !!window[key];
    }
    //
    const subscriptionDictionary: Dictionary<Subscription> = {};
    const subscription = `${key}-${prop}-schedule-${+new Date()}`;

    // && window[key].hasOwnProperty(prop)
    if (window[key]) {
      clearSubscription(subscription, subscriptionDictionary);
      subscriptionDictionary[subscription] = asapScheduler
        .schedule(() => {
          callback(window[key]);
          clearSubscription(subscription, subscriptionDictionary);
        }, delay);
    } else {
      let i = 0;
      const intervalId = `${key}-${prop}-${+new Date()}`;
      subscriptionDictionary[intervalId] = interval(100)
        .subscribe(() => {
          // && window[key].hasOwnProperty(prop)
          if (window[key]) {
            clearSubscription(intervalId, subscriptionDictionary);
            subscriptionDictionary[subscription] = asapScheduler
              .schedule(() => {
                callback(window[key]);
                clearSubscription(subscription, subscriptionDictionary);
              }, delay);
          } else if (i >= 100) {
            clearSubscription(intervalId, subscriptionDictionary);
          }
          i++;
        });
    }
  }

  /**
   *
   */
  bookCall(organization?: string, room?: string): void {
    this.event.emit({ type: 'book-a-call', data: { organization, room } });
  }

  /**
   *
   */
  openChat(callback?: () => void): void {
    // load lib (if not already loaded)
    this.windowObject('zE', 'activate', (zendesk) => {
      // identify user
      if (this.user.signedIn) {
        zendesk('webWidget', 'identify', {
          name: `${this.user.current?.first_name} ${this.user.current?.last_name}`,
          email: this.user.current?.email,
        });
      } else {
        zendesk('webWidget', 'logout');
      }

      const now = moment();

      const time = parseInt(now.clone().format('HH'));

      const dayOfWeek = now.day();
      const isWeekend = (dayOfWeek === 6) || (dayOfWeek === 0);
      // 6 = Saturday, 0 = Sunday

      if (time >= 10 && time < 18 && !isWeekend) {
        if (zendesk?.activate) {
          // open chat
          zendesk.activate({ hideOnClose: true });
        }

        // callback
        if (callback) {
          callback();
        }
      } else {
        this.modal
          .open(`
          <p>Our agents aren’t available at this time.</p>
          <p>You can chat with us between:
          <br>
          <ul>
            <li>Monday to Thursday, 10am - 6pm;</li><br>
            <li>Friday, 10am - 5pm;</li>
          </ul>
          </p>
          <!-- <p>Why not book a call with us?</p> -->
          `, {
            html: true,
            // ok: 'Book a call',
            // okTheme: 'flat-primary',
            // cancel: 'Cancel',
          })
          .then((response) => {
            if (response.action === 'ok') {
              this.bookCall('proportunity', 'chat-with-us');
            }
          });
      }
    });
  }
}
